import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Link2, Undo2 } from 'lucide-react';
import Panel from './Panel.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { getHangarReview, startHangarReview, withdrawHangarReview } from '@/services/deals.service.js';

// THE HANGAR ANNUAL REVIEW CARD (main batch 2026-11-01, N1; David: "a fresh application every year as a quick 'review values,
// update, sign' flow, not a full application."). On a renewal hangar deal: start the client's review of last year's
// application, see where it stands, copy the link to send from your own mailbox (the property line's way: TRACON prepares,
// the broker sends), withdraw a link, and once signed see what the client changed and what to update elsewhere. Every word and
// state is the server's (routes/hangar-review-broker.routes.js); the card derives nothing. It is an ask, never a gate.
// A new-business deal (no prior term) renders nothing.

function usDate(ymd) {
  if (typeof ymd !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return '';
  return `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}/${ymd.slice(0, 4)}`;
}
function shown(kind, v) {
  if (kind !== 'money' || v === '') return v;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? `$${n.toLocaleString('en-US')}` : v;
}
const BADGE = { ready: ['warning', 'Not sent'], live: ['info', 'Waiting on the client'], signed: ['success', 'Signed'], ended: ['outline', 'No link out'] };

function copy(text, what) {
  if (typeof navigator === 'undefined' || !navigator.clipboard) { toast.error('Clipboard unavailable in this browser'); return; }
  navigator.clipboard.writeText(text).then(() => toast.success(`Copied ${what}`)).catch(() => toast.error('Copy failed'));
}
function messageFor(card) {
  const insured = (card.prior && card.prior.insured) || 'your hangar';
  return `Hi,\n\nYour hangar policy for ${insured} is coming up for renewal. Instead of a new application, please look over last year's answers, change anything that is different, and sign here:\n\n${card.review.link}\n\nIt takes a few minutes. Thank you!`;
}

export default function HangarAnnualReviewPanel({ dealId, accountId }) {
  const [card, setCard] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    setError(null);
    getHangarReview(dealId).then((r) => setCard(r)).catch((e) => setError((e && e.body && e.body.error) || 'Could not read the annual review.'));
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <Panel title="Annual review"><p className="text-sm text-text-muted" data-hangar-review-card="error">Could not check the annual review: {error} <Button variant="link" size="sm" onClick={load}>Check again</Button></p></Panel>;
  if (!card || !card.applies) return null;

  const insured = (card.prior && card.prior.insured) || 'the client';
  const review = card.review;
  const [variant, label] = BADGE[card.state] || ['outline', card.state];

  async function start() {
    setBusy(true); setRefusal(null);
    try {
      const out = await startHangarReview(dealId);
      setCard(out);
      toast.success(`Annual review link made for ${insured}. Copy it and send it.`);
    } catch (e) { setRefusal((e && e.body && e.body.error) || 'The review was not started.'); load(); }
    finally { setBusy(false); }
  }
  async function withdraw() {
    setBusy(true); setRefusal(null);
    try {
      const out = await withdrawHangarReview(dealId, review.id, reason);
      setCard(out); setWithdrawOpen(false); setReason('');
      toast.success('The review link was withdrawn; it now says it has done its job.');
    } catch (e) { setRefusal((e && e.body && e.body.error) || 'The link was not withdrawn.'); load(); }
    finally { setBusy(false); }
  }

  return (
    <Panel title="Annual review">
      <div className="space-y-3 text-sm" data-hangar-review-card={card.state}>
        <p className="text-text-muted">The client looks over last year&apos;s hangar application, changes what is different, and signs this year&apos;s. It never holds the renewal.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={variant}>{label}</Badge>
          <span>{card.words}</span>
        </div>
        {refusal ? <p className="rounded-md bg-warning-soft px-3 py-2 text-warning" role="alert">{refusal}</p> : null}

        {(card.state === 'ready' || card.state === 'ended') ? (
          <Button onClick={start} disabled={busy} data-action="start-hangar-review"><Link2 className="mr-1.5 h-4 w-4" />Make the annual review link for {insured}</Button>
        ) : null}

        {card.state === 'live' && review ? (
          <div className="space-y-2">
            <p className="break-all font-mono text-xs text-text-muted">{review.link}</p>
            <p className="text-xs text-text-dim">Link good until {usDate(String(review.expires_at || '').slice(0, 10))}. Send it from your own mailbox or by text.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => copy(review.link, 'the review link')}><Copy className="mr-1.5 h-4 w-4" />Copy the review link</Button>
              <Button variant="outline" size="sm" onClick={() => copy(messageFor(card), 'the message with the link')}><Copy className="mr-1.5 h-4 w-4" />Copy a message with the link</Button>
              <Button variant="ghost" size="sm" onClick={() => setWithdrawOpen(true)} disabled={busy}><Undo2 className="mr-1.5 h-4 w-4" />Withdraw the review link</Button>
            </div>
          </div>
        ) : null}

        {card.state === 'signed' && review ? (
          <div className="space-y-2">
            {review.changes.length ? (
              <div>
                <p className="font-medium">What {review.signer_name} changed</p>
                <ul className="ml-5 list-disc">
                  {review.changes.map((c) => <li key={c.key}>{c.label}: {c.now === '' ? 'removed' : shown(c.kind, c.now)} (was {shown(c.kind, c.was)})</li>)}
                </ul>
              </div>
            ) : <p>Nothing changed from last year.</p>}
            {review.note ? <p><span className="font-medium">The client&apos;s note:</span> {review.note}</p> : null}
            {review.follow_ups.map((f) => (
              <div key={f} className="rounded-md bg-warning-soft px-3 py-2 text-warning">
                <p>{f}</p>
                {accountId ? <Link className="font-medium underline" to={`/account/${encodeURIComponent(accountId)}`} data-action="open-account-to-update">Open the account to update it</Link> : null}
              </div>
            ))}
            <p className="text-xs text-text-dim">
              {review.filed ? `Filed on the Paperwork tab as ${review.file_name}.` : `Not filed on the Paperwork tab yet${review.file_name ? ` (${review.file_name})` : ''}: drop the signed PDF there as Signed application.`}
              {' '}This year&apos;s application now shows in the Submission panel.
            </p>
          </div>
        ) : null}
      </div>

      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Withdraw the review link for {insured}?</DialogTitle>
            <DialogDescription>The link will say it has done its job. You can send a new one after.</DialogDescription>
          </DialogHeader>
          <label className="block text-sm">
            <span className="text-text-muted">Why (kept on the review)</span>
            <textarea className="mt-1 w-full rounded-md border border-border bg-surface p-2 text-sm" rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} data-field="withdraw-reason" />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawOpen(false)}>Keep the link out</Button>
            <Button onClick={withdraw} disabled={busy || !reason.trim()}>Withdraw the link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
